import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { EmptyState, ListSkeleton, PageHeader, type Crumb } from '@routes-plan/ui';
import { useT } from '../../../i18n';
import { AdminPage, ContentCard, FilterRow, FormActions } from './AdminUi';

export interface ChecklistItem {
  id: number;
  name: string;
  secondary?: string;
}

interface Props {
  title: string;
  description?: string;
  breadcrumbs?: Crumb[];
  /** Filtros arriba de la lista (por ejemplo, la sede). */
  filters?: React.ReactNode;
  /** Mensaje cuando no hay elementos (o falta elegir un filtro). */
  emptyTitle: string;
  emptyDescription?: string;
  emptyIcon?: React.ReactNode;
  items: ChecklistItem[];
  selected: number[];
  onToggle: (id: number, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  loading?: boolean;
  cancelTo: string;
  onSave: () => void;
  saving?: boolean;
  allowed?: boolean;
  deniedTooltip?: string;
  saveDisabled?: boolean;
  notice?: React.ReactNode;
}

/**
 * Pantalla de asignación: lista de casillas (sedes o rutas) con "Todas", contador y Guardar.
 * Las casillas se reparten en columnas según el ancho y nunca desbordan la pantalla.
 */
const AssignChecklist: React.FC<Props> = ({
  title,
  description,
  breadcrumbs,
  filters,
  emptyTitle,
  emptyDescription,
  emptyIcon,
  items,
  selected,
  onToggle,
  onToggleAll,
  loading,
  cancelTo,
  onSave,
  saving,
  allowed = true,
  deniedTooltip,
  saveDisabled,
  notice,
}) => {
  const t = useT();
  const count = items.filter((i) => selected.includes(i.id)).length;
  const all = items.length > 0 && count === items.length;
  return (
    <AdminPage narrow>
      <PageHeader title={title} description={description} breadcrumbs={breadcrumbs} />
      {notice}
      <ContentCard>
        {filters && <FilterRow sx={{ mb: 2 }}>{filters}</FilterRow>}
        {loading ? (
          <ListSkeleton rows={4} columns={1} />
        ) : items.length === 0 ? (
          <EmptyState title={emptyTitle} description={emptyDescription} icon={emptyIcon} dense />
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={all}
                    indeterminate={count > 0 && !all}
                    onChange={(e) => onToggleAll(e.target.checked)}
                    disabled={!allowed}
                  />
                }
                label={<Typography variant="subtitle2">{t('common.all')}</Typography>}
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                {t('users.selectedCount', { count, total: items.length })}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))',
                columnGap: 2,
                maxHeight: 'min(60vh, 560px)',
                overflowY: 'auto',
              }}
            >
              {items.map((item) => {
                const checked = selected.includes(item.id);
                return (
                  <FormControlLabel
                    key={item.id}
                    sx={{ minWidth: 0, mr: 0, py: 0.25 }}
                    control={<Checkbox checked={checked} onChange={(e) => onToggle(item.id, e.target.checked)} disabled={!allowed} />}
                    label={
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {item.name}
                        </Typography>
                        {item.secondary && (
                          <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {item.secondary}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                );
              })}
            </Box>
          </>
        )}
      </ContentCard>
      <FormActions
        cancelTo={cancelTo}
        submit={false}
        onSave={onSave}
        saving={saving}
        allowed={allowed}
        deniedTooltip={deniedTooltip}
        disabled={saveDisabled}
      />
    </AdminPage>
  );
};

export default AssignChecklist;
